import { A } from '@ember/array';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, setProperties } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { sort } from '@ember/object/computed';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { isBlank, isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import { all, dropTask } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import round from 'ember-math-helpers/helpers/round';
import { eq, gte } from 'ember-truth-helpers';
import LocationsTableBulkModalAssignment from 'garaje/components/location-overview/locations/locations-table/bulk-modal-assignment';
import type { SortDirection } from 'garaje/components/sortable-header-column';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import DropdownMenu from 'garaje/components/ui/classic/dropdown-menu';
import UiButton from 'garaje/components/ui-button';
import UiInput from 'garaje/components/ui-input';
import dateFormat from 'garaje/helpers/date-format';
import formattedCurrency from 'garaje/helpers/formatted-currency';
import type AverageMonthlyVisitorCount from 'garaje/models/average-monthly-visitor-count';
import type Group from 'garaje/models/group';
import type SkinnyLocation from 'garaje/models/skinny-location';
import type ContextSwitcherService from 'garaje/services/context-switcher';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type WindowLocationService from 'garaje/services/window-location';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface LocationsTableWithGroupsSignature {
  Args: {
    /**
     * List of available groups to be assigned
     */
    groups: Group[];
    /**
     * List of skinny-locations
     */
    locations: SkinnyLocation[];
    averageMonthlyVisitorCount: AverageMonthlyVisitorCount[];
    switchLocation: (location: SkinnyLocation, route: string) => void;
    canManageGroups: boolean;
  };
  Element: HTMLDivElement;
}

interface LocationRow {
  location: SkinnyLocation;
  averageMonthlyVisitorCount: number;
}

/**
 * List of locations with group assignment capabilities
 */
export default class LocationsTableWithGroups extends Component<LocationsTableWithGroupsSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;
  @service declare localStorage: LocalStorageService;
  @service declare windowLocation: WindowLocationService;
  @service declare contextSwitcher: ContextSwitcherService;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare metrics: MetricsService;

  @tracked sortField = 'name';
  @tracked sortDirection: SortDirection = 'asc';

  @tracked selectedLocations: SkinnyLocation[] = [];
  @tracked focusedLocation: SkinnyLocation | null = null;

  @tracked showBulkModalAssignment = false;
  @tracked searchFilter = '';
  /**
   * Groups sorted by `name` to show in the dropdown selector
   */
  sortProperties = ['name'];
  @sort('args.groups', 'sortProperties') sortedGroups!: Group[];

  /**
   * This CP filters the passed `@locations` using the `this.searchFilter`
   *
   * @return {Array<Location>}
   */
  @computed('args.locations.@each.nameWithCompanyName', 'searchFilter')
  get filteredLocations(): SkinnyLocation[] {
    if (isBlank(this.searchFilter)) {
      return this.args.locations;
    }
    const lowerSearchFilter = this.searchFilter.toLowerCase();
    return this.args.locations.filter((location) =>
      location.nameWithCompanyName.toLowerCase().includes(lowerSearchFilter),
    );
  }

  @computed(
    'averageMonthlyVisitorCountByLocation',
    'filteredLocations.@each.{groups,nameWithCompanyName}',
    'sortDirection',
    'sortField',
  )
  get sortedLocations(): LocationRow[] {
    const rows = this.filteredLocations.map((location) => ({
      location,
      averageMonthlyVisitorCount: this.averageMonthlyVisitorCountByLocation[location.id]?.count ?? 0,
    }));

    const sortField =
      this.sortField === 'averageMonthlyVisitorCount' ? 'averageMonthlyVisitorCount' : `location.${this.sortField}`;

    let sorted;
    // handle null groups
    if (this.sortField === 'group.name') {
      sorted = rows.toSorted((a, b) => {
        const aName = a.location.group?.name ?? '';
        const bName = b.location.group?.name ?? '';
        return aName.localeCompare(bName);
      });
    } else {
      sorted = A(rows).sortBy(sortField);
    }

    if (this.sortDirection === 'desc') {
      sorted.reverse();
    }
    return sorted;
  }

  get averageMonthlyVisitorCountByLocation(): Record<string, AverageMonthlyVisitorCount> {
    return this.args.averageMonthlyVisitorCount.reduce<Record<string, AverageMonthlyVisitorCount>>((acc, curr) => {
      acc[curr.belongsTo('location').id()] = curr;
      return acc;
    }, {});
  }

  get allCheckState(): string {
    if (this.selectedLocations.length === this.args.locations.length) {
      return 'checked';
    } else if (this.selectedLocations.length > 0) {
      return 'indeterminate';
    }
    return '';
  }

  /**
   * This sets a `min-height` value to the locations table so when filtering
   * it doesn't jump and keep a minimum height
   */
  get minHeight(): number {
    const perRow = 63;
    return this.args.locations.length * perRow;
  }

  @computed('selectedLocations.@each.group')
  get anyRemovableLocation(): boolean {
    return this.selectedLocations.some((location) => isPresent(location.group));
  }

  get workplaceSubscription(): boolean {
    return (
      !!this.state.features?.canAccessWorkplaceApplication || !!this.state.features?.canAccessProtectLegacyFeatures
    );
  }

  get canManageGroups(): boolean {
    return this.args.groups.length > 0 && this.args.canManageGroups;
  }

  removeFromGroupTask = dropTask(async (location: SkinnyLocation | null) => {
    const locationsWithGroup = location
      ? [location]
      : this.selectedLocations.filter((location) => isPresent(location.group));
    if (locationsWithGroup.length === 0) {
      return;
    }
    try {
      await all(
        locationsWithGroup.map((location) => {
          const group = location.group;
          A(group?.locations.content).removeObject(location);
          return group?.save();
        }),
      );

      // If the real location is already loaded, reload it.
      await this._reloadPresentRealLocationsTask.perform(locationsWithGroup);

      const message = `${pluralize(locationsWithGroup.length, 'location')} removed from groups.`;
      this.flashMessages.showAndHideFlash('success', message);
      if (!location) {
        this.selectedLocations = [];
      }
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  assignGroupTask = dropTask(
    waitFor(async (selectedGroup: Group) => {
      const selectedLocations = this.focusedLocation ? [this.focusedLocation] : this.selectedLocations;

      const locationsWithGroup = selectedLocations.filter((location) => isPresent(location.group));
      try {
        if (this.focusedLocation) {
          if (this.focusedLocation.group) {
            this.metrics.trackEvent('Group Action Taken', {
              action: 'group_members_removed',
              target_group: this.focusedLocation.group.name,
              target_group_members: [this.focusedLocation.name],
            });
          }

          this.metrics.trackEvent('Group Action Taken', {
            action: 'group_members_added',
            target_group: selectedGroup.name,
            target_group_members: [this.focusedLocation.name],
          });
        }

        // Remove any existing group associations
        await all(
          locationsWithGroup.map((location) => {
            const group = location.group;
            A(group?.locations.content).removeObject(location);
            return group?.save();
          }),
        );

        selectedLocations.map((location) => A(selectedGroup.locations.content).addObject(location));

        await selectedGroup.save();

        // If the real location is already loaded, reload it.
        await this._reloadPresentRealLocationsTask.perform(selectedLocations);

        this.flashMessages.showAndHideFlash('success', 'Saved');
        this.showBulkModalAssignment = false;

        if (this.focusedLocation) {
          this.focusedLocation = null;
        } else {
          this.selectedLocations = [];
        }
      } catch (e) {
        const errorText = parseErrorForDisplay(e);
        this.flashMessages.showAndHideFlash('error', errorText);
      }
    }),
  );

  @action
  sortByGroups(sortField: string, sortDirection: SortDirection): void {
    setProperties(this, { sortField, sortDirection });
  }

  @action
  switchLocation(location: SkinnyLocation, route: string, e: Event): void {
    if (e && e.preventDefault) e.preventDefault();
    this.args.switchLocation(location, route);
  }

  @action
  onLocationSelected(location: SkinnyLocation): void {
    if (this.selectedLocations.includes(location)) {
      this.selectedLocations = this.selectedLocations.filter((selectedLocation) => selectedLocation !== location);
    } else {
      this.selectedLocations = [...this.selectedLocations, location];
    }
  }

  @action
  toggleAllSelected(e: Event): void {
    const target = <HTMLInputElement>e.target;
    if (this.selectedLocations.length === 0) {
      this.selectedLocations = [...this.args.locations];
    } else {
      this.selectedLocations = [];
    }
    target.checked = this.selectedLocations.length > 0;
    target.indeterminate = this.allCheckState === 'indeterminate';
  }

  @action
  addToGroup(location: SkinnyLocation): void {
    this.focusedLocation = location;
    this.showBulkModalAssignment = true;
  }

  /**
   * When a group is assigned or removed, if the real location (not the `skinnyLocation`) is already in the store
   * we need to reload it so the association matches.
   */
  _reloadPresentRealLocationsTask = dropTask(async (locations: SkinnyLocation[]) => {
    for (const location of locations) {
      const realLocation = this.store.peekRecord('location', location.id);
      if (realLocation) {
        await realLocation.reload();
      }
    }
  });

  <template>
    <div ...attributes>
      {{#if (gte @locations.length 9)}}
        <div class="mt-8 mb-3 w-1/3">
          <label for="groups-locations-search" class="sr-only">Locations filter</label>
          <UiInput
            data-test-groups-location-search
            @id="groups-locations-filter"
            class="pageSearch border p-2 icon-search max-w-full"
            @type="search"
            @placeholder="Search locations"
            @value={{this.searchFilter}}
            @update={{fn (mut this.searchFilter)}}
          />
        </div>
      {{/if}}

      {{! template-lint-disable style-concatenation }}
      <table data-display-table style="height: {{this.minHeight}}px;">
        <thead class="relative">
          <div
            class="flex items-center h-10 max-h-10 w-full absolute bg-white ml-[13px]
              {{unless this.selectedLocations.length 'hidden'}}"
          >
            <span class="text-carbon-50 text-sm ml-4" data-test-total-locations-selected>
              {{pluralize this.selectedLocations.length "location"}}
              selected
            </span>

            <span class="flex flex-1"></span>

            <UiButton
              data-test-bulk-assign-group
              {{on "click" (fn (mut this.showBulkModalAssignment) true)}}
              disabled={{this.removeFromGroupTask.isRunning}}
              class="btn btn--link btn--small !text-gem-50 mb-1"
            >
              Assign to group
            </UiButton>
            {{#if this.anyRemovableLocation}}
              <UiButton
                data-test-bulk-remove-group
                {{on "click" (fn this.removeFromGroupTask.perform null)}}
                class="btn btn--link btn--small !text-gem-50 mb-1 ml-4"
                disabled={{this.removeFromGroupTask.isRunning}}
              >
                {{if this.removeFromGroupTask.isRunning "Removing..." "Remove from group"}}
              </UiButton>
            {{/if}}
          </div>

          <tr data-test-location-overview-cols class="h-10 max-h-10">
            {{#if this.canManageGroups}}
              <th class="p-0 pb-2" style="width: 22px;">
                {{#if this.removeFromGroupTask.isRunning}}
                  <img
                    data-test-loading
                    alt="loading"
                    src="/assets/images/loading-grey.gif"
                    style="width: 12px; margin-left: 6px;"
                  />
                {{else}}
                  <input
                    data-test-groups-locations-all-selector
                    type="checkbox"
                    {{! @glint-expect-error - Not an attribute that can be set directly. Check if this works }}
                    indeterminate={{eq this.allCheckState "indeterminate"}}
                    checked={{eq this.allCheckState "checked"}}
                    {{on "click" this.toggleAllSelected}}
                  />
                {{/if}}
              </th>
            {{/if}}

            <th class="py-2 w-1/6">
              <SortableHeaderColumn
                @name="Location name"
                @isActive={{eq this.sortField "name"}}
                @sortDirection={{this.sortDirection}}
                @sortField="name"
                @sortAction={{this.sortByGroups}}
              />
            </th>
            <th class="py-2">
              <SortableHeaderColumn
                @name="Location created"
                @isActive={{eq this.sortField "createdAt"}}
                @sortDirection={{this.sortDirection}}
                @sortField="createdAt"
                @sortAction={{this.sortByGroups}}
              />
            </th>
            <th class="py-2">
              <SortableHeaderColumn
                @name="Last sign-in"
                @isActive={{eq this.sortField "lastEntryAt"}}
                @sortDirection={{this.sortDirection}}
                @sortField="lastEntryAt"
                @sortAction={{this.sortByGroups}}
              />
            </th>
            <th class="py-2">
              <SortableHeaderColumn
                @name="Average monthly visitors"
                @isActive={{eq this.sortField "averageMonthlyVisitorCount"}}
                @sortDirection={{this.sortDirection}}
                @sortField="averageMonthlyVisitorCount"
                @sortAction={{this.sortByGroups}}
              />
            </th>

            <th class="py-2" data-test-workplace-size-column>
              <SortableHeaderColumn
                @name="Workplace size"
                @isActive={{eq this.sortField "workplaceUtilizationDetail.area"}}
                @sortDirection={{this.sortDirection}}
                @sortField="workplaceUtilizationDetail.area"
                @sortAction={{this.sortByGroups}}
              />
            </th>
            <th class="py-2" data-test-lease-cost-month-column>
              <SortableHeaderColumn
                @name="Lease cost/month"
                @isActive={{eq this.sortField "workplaceUtilizationDetail.monthlyRent"}}
                @sortDirection={{this.sortDirection}}
                @sortField="workplaceUtilizationDetail.monthlyRent"
                @sortAction={{this.sortByGroups}}
              />
            </th>
            <th class="py-2" data-test-lease-end-date-column>
              <SortableHeaderColumn
                @name="Lease end date"
                @isActive={{eq this.sortField "workplaceUtilizationDetail.leaseExpirationDate"}}
                @sortDirection={{this.sortDirection}}
                @sortField="workplaceUtilizationDetail.leaseExpirationDate"
                @sortAction={{this.sortByGroups}}
              />
            </th>

            {{#if this.canManageGroups}}
              <th data-test-groups-column class="py-2">
                <SortableHeaderColumn
                  @name="Group"
                  @isActive={{eq this.sortField "group.name"}}
                  @sortDirection={{this.sortDirection}}
                  @sortField="group.name"
                  @sortAction={{this.sortByGroups}}
                />
              </th>
            {{/if}}
            <th class="py-2">
            </th>
          </tr>
        </thead>
        <tbody>
          {{#each this.sortedLocations as |row|}}
            <tr class="w-full h-14" data-test-location-row={{row.location.id}}>
              {{#if this.canManageGroups}}
                <td class="p-0 align-middle border-t pt-0.5" style="width: 22px;">
                  <input
                    data-test-group-selection-checkbox
                    disabled={{this.removeFromGroupTask.isRunning}}
                    type="checkbox"
                    checked={{includes row.location this.selectedLocations}}
                    {{on "click" (fn this.onLocationSelected row.location)}}
                  />
                </td>
              {{/if}}
              <td class="p-0 w-1/6 border-t align-middle" data-test-name>
                <div class="pl-4">
                  <a
                    data-test-link-to-location
                    {{on "click" (fn this.switchLocation row.location "application")}}
                    class="text-carbon-70 hover:grayDark no-underline
                      {{if row.location.disabledToEmployeesAt 'opacity-50'}}"
                    href="#"
                  >
                    {{row.location.nameWithCompanyName}}
                    {{#if row.location.disabledToEmployeesAt}}
                      <span data-test-location-disabled>(Disabled)</span>
                    {{/if}}
                  </a>
                </div>
              </td>
              <td class="p-0 pl-4 border-t align-middle" data-test-created-at>
                {{dateFormat row.location.createdAt "MMM d, yyyy"}}
              </td>
              <td class="p-0 pl-4 border-t align-middle" data-test-last-signed-in>
                {{dateFormat row.location.lastEntryAt "MMM d, yyyy"}}
              </td>
              <td class="p-0 pl-4 border-t align-middle" data-test-average-monthly-visitors>
                {{round row.averageMonthlyVisitorCount}}
              </td>

              <td class="p-0 pl-4 border-t align-middle" data-test-workplace-size>
                {{#if row.location.workplaceUtilizationDetail.areaSqFt}}
                  {{row.location.workplaceUtilizationDetail.areaSqFt}}
                  sq ft
                {{/if}}
              </td>
              <td class="p-0 pl-4 border-t align-middle" data-test-lease-cost>
                {{#if row.location.workplaceUtilizationDetail.monthlyRent}}
                  {{formattedCurrency
                    row.location.workplaceUtilizationDetail.monthlyRent
                    (hash currency=row.location.workplaceUtilizationDetail.monthlyRentCurrencyCode)
                  }}
                {{/if}}
              </td>

              <td class="p-0 pl-4 border-t align-middle" data-test-lease-end-date>
                {{#if row.location.workplaceUtilizationDetail.leaseExpirationDate}}
                  {{dateFormat row.location.workplaceUtilizationDetail.leaseExpirationDate "MMM d, yyyy"}}
                {{/if}}
              </td>

              {{#if this.canManageGroups}}
                <td class="p-0 pl-4 align-middle border-t">
                  <span data-test-group>{{row.location.group.name}}</span>
                </td>
              {{/if}}
              <td class="border-t align-middle">
                <DropdownMenu @right="-41" @top="12" style="text-align: left !important;">
                  <:trigger>
                    <div class="mx-2 font-bold text-xl cursor-pointer" data-test-location-menu-trigger>
                      ···
                    </div>
                  </:trigger>
                  <:content as |itemClass|>
                    <UiButton
                      class="{{itemClass}}"
                      data-test-view-settings
                      {{on "click" (fn this.switchLocation row.location "manage.location-settings")}}
                    >
                      View settings
                    </UiButton>
                    {{#if row.location.group}}
                      <UiButton
                        class="{{itemClass}}"
                        data-test-remove-from-group
                        {{on "click" (fn this.removeFromGroupTask.perform row.location)}}
                      >
                        Remove from group
                      </UiButton>
                    {{/if}}
                    {{#if this.canManageGroups}}
                      <UiButton
                        class="{{itemClass}}"
                        data-test-add-to-group
                        {{on "click" (fn this.addToGroup row.location)}}
                      >
                        Add to group
                      </UiButton>
                    {{/if}}
                  </:content>
                </DropdownMenu>
              </td>
            </tr>
          {{else}}
            <tr>
              <td colspan="100%" class="text-carbon-50 text-center pt-6">
                No results found
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>

      {{#if this.showBulkModalAssignment}}
        <LocationsTableBulkModalAssignment
          class="max-w-sm"
          @closeModal={{fn (mut this.showBulkModalAssignment) false}}
          @assignGroupTask={{this.assignGroupTask}}
          @groups={{this.sortedGroups}}
        />
      {{/if}}
    </div>
  </template>
}
